import React, { useEffect, useRef, useState } from 'react';
import { CartesianGrid, Line, LineChart, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { PointInTimeEarnings } from '../../earnings';
import { moneyFormatter } from '../../utils';

import { formatSeconds, formatValue, oneMinuteTicks } from './chart-utils';

interface EarningsRateChartProps {
  chart_data: PointInTimeEarnings[];
}

export const EarningsRateChart: React.FC<EarningsRateChartProps> = ({ chart_data }) => {
  const ticks = oneMinuteTicks(chart_data);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-[400px] w-full">
      {size.width > 0 && size.height > 0 && (
        <LineChart width={size.width} height={size.height} data={chart_data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="seconds"
            label={{ value: 'Simulation Time (minutes)', position: 'insideBottom', offset: -5 }}
            height={35}
            domain={['auto', 'auto']}
            tickFormatter={formatSeconds}
            ticks={ticks}
          />
          <YAxis
            label={{
              value: 'Earnings Rate (EUR/s)',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { textAnchor: 'middle' },
            }}
            width={80}
            tickFormatter={formatValue}
          />
          <Tooltip active={true} content={<EarningsRateTooltip />} />
          <Line type="stepAfter" dataKey="value" stroke="#82ca9d" />
        </LineChart>
      )}
    </div>
  );
};

interface EarningsRateTooltipProps extends TooltipProps<number, number> {
  active?: boolean;
  payload?: {
    value: number;
  }[];
  label?: number;
}

const EarningsRateTooltip = ({ active, payload, label }: EarningsRateTooltipProps) => {
  const value = payload && payload.length ? payload[0].value : 0;

  if (active && payload && payload.length) {
    return (
      <div className="bg-agility-light/30 dark:bg-agility-dark/30 text-text-primary p-1.5 rounded border border-agility">
        {label !== undefined && <p>{`Time: ${formatSeconds(label)}`}</p>}
        <p>{`Earnings Rate: ${moneyFormatter.format(value)}/s`}</p>
      </div>
    );
  }

  return null;
};
